import React from "react"
import { useTranslation } from "react-i18next"
import { Formik } from "formik"
import {
  Button,
  Form,
  FormGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap"
import * as Yup from "yup"
import CustomLabel from "components/Common/CustomLabel"
import CustomField from "components/Common/CustomField"
import { useDispatch } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import { resetUserPassword } from "../../../store/actions/userAction"
import { Eye, EyeOff } from "lucide-react"

const initialValues = {
  new_password: "",
  new_password_confirmation: "",
}

const PasswordResetModal = ({ isOpen, toggle, user }) => {
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = React.useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false)
  const dispatch = useDispatch()

  const validationSchema = Yup.object().shape({
    new_password: Yup.string()
      .required(t("Field is required"))
      .min(8, t("Password must be at least 8 characters long"))
      .matches(/[A-Z]/, t("Must contain an uppercase letter (A-Z)"))
      .matches(/[a-z]/, t("Must contain a lowercase letter (a-z)"))
      .matches(/\d/, t("Must contain a number (0-9)"))
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        t("Must contain a special character (@, #, !, ...)")
      ),
    new_password_confirmation: Yup.string()
      .oneOf([Yup.ref("new_password"), null], t("Passwords must match"))
      .required(t("Field is required")),
  })

  const onSubmit = values => {
    const payload = {
      user_uuid: user.uuid,
      password: values.new_password_confirmation,
    }
    dispatch(resetUserPassword(payload))
      .then(action => {
        const { statusCode, success, message } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(err => {})
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {t("Reset Password")} {user?.full_name}
      </ModalHeader>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={onSubmit}
      >
        {({ isValid, errors, touched, handleSubmit }) => (
          <>
            <ModalBody>
              <Form>
                <FormGroup>
                  <CustomLabel
                    htmlFor="new_password"
                    label={t("New Password")}
                    isRequired={true}
                  />
                  <div className="relative">
                    <CustomField
                      name="new_password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("Enter your new password")}
                      touched={touched}
                      errors={errors}
                      className="no-eye-icon"
                    />
                    <button
                      type="button"
                      className="password-toggle tr_btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </FormGroup>
                <FormGroup>
                  <CustomLabel
                    htmlFor="new_password_confirmation"
                    label={t("Confirm New Password")}
                    isRequired={true}
                  />
                  <div className="relative">
                    <CustomField
                      name="new_password_confirmation"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder={t("Enter your new password again")}
                      touched={touched}
                      errors={errors}
                      className="no-eye-icon"
                    />
                    <button
                      type="button"
                      className="password-toggle tr_btn"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                </FormGroup>
              </Form>
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                onClick={handleSubmit}
                disabled={!isValid}
              >
                {t("Submit")}
              </Button>
              <Button color="secondary" onClick={toggle}>
                {t("Cancel")}
              </Button>
            </ModalFooter>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default PasswordResetModal
